import React, { useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import useToast from '../hooks/useToast';

const Scan = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [customId, setCustomId] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  // ✅ React Query: Fetch devices
  const {
    data: devices = [],
    isLoading: devicesLoading,
    isError: devicesError,
  } = useApiQuery(
    ['devices'],
    () => api.get('/devices').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError('Failed to load devices'),
    }
  );

  // ✅ Auto-select first device when data loads
  React.useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId) {
  setSelectedDeviceId(devices[0]._id);
}
}, [devices, selectedDeviceId]);

  // ✅ Memoize selected device
const selectedDevice = useMemo(() => {
  return devices.find(d => d._id === selectedDeviceId);
}, [devices, selectedDeviceId]);

  // ✅ React Query: Scan mutation
  const scanMutation = useApiMutation(
    (data) => api.post('/sessions/scan', data).then(res => res.data),
    {
      onSuccess: (data, variables) => {
        const type = variables.type.toUpperCase();
        const time = new Date(data.entryTime).toLocaleTimeString();
        showSuccess(`${type} recorded at ${time}`);
        setCustomId('');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Scan failed');
      },
    }
  );

  // ✅ Handle scan
  const handleScan = useCallback((type) => {
    if (!customId.trim()) {
      showError('Please enter a Custom ID');
      return;
    }
    if (!selectedDeviceId) {
  showError('No device selected');
  return;
}
    if (!selectedDevice?.eventId) {
      showError('Device not associated with any event');
      return;
    }

    scanMutation.mutate({
      customId: customId.trim(),
    deviceId: selectedDeviceId,
      eventId: selectedDevice.eventId,
      type,
    });
 }, [customId, selectedDeviceId, selectedDevice, scanMutation, showError]);

  // ✅ Loading state
  if (devicesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading devices...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (devicesError) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Failed to load devices. Please refresh the page.
        </div>
      </div>
    );
  }

  const isLoading = scanMutation.isPending;

  return (
    <div className="p-6 max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">Scan Entry / Exit</h1>
      
      <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6 space-y-4">
        {/* Device Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Device</label>
         <select
  value={selectedDeviceId}
  onChange={(e) => setSelectedDeviceId(e.target.value)}
            className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
            disabled={isLoading || devices.length === 0}
          >
            {devices.length === 0 ? (
              <option value="">No devices available</option>
            ) : (
              devices.map(device => (
                <option key={device._id} value={device._id}>
                  {device.name}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Custom ID Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User Custom ID</label>
          <input
            type="text"
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            placeholder="Enter user's custom ID"
            className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
            autoFocus
            disabled={isLoading}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4 pt-2">
          <button
            onClick={() => handleScan('entry')}
          disabled={isLoading || !selectedDeviceId || !customId.trim()}
            className="flex-1 bg-green-600 text-white py-2.5 rounded-[8px] font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isLoading ? 'Processing...' : 'Entry'}
          </button>
          <button
            onClick={() => handleScan('exit')}
           disabled={isLoading || !selectedDeviceId || !customId.trim()}
            className="flex-1 bg-red-600 text-white py-2.5 rounded-[8px] font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isLoading ? 'Processing...' : 'Exit'}
          </button>
        </div>

        {/* Status info */}
        <div className="text-xs text-gray-400 mt-2">
          {selectedDevice && (
            <p>Device: <span className="font-medium text-gray-600">{selectedDevice.name}</span></p>
          )}
          {selectedDevice?.eventId && (
            <p>Event: <span className="font-medium text-gray-600">
              {typeof selectedDevice.eventId === 'object' 
                ? selectedDevice.eventId.name 
                : selectedDevice.eventId}
            </span></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scan;